import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Bomb, Zap, Anchor, Sparkles, Loader2, AlertTriangle, ArrowUpRight, Download,
  Target, ShieldOff, Network, RefreshCw,
} from "lucide-react";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";

/**
 * Systemic Analysis tab — cross-cutting executive lecture. Aggregates Top 3
 * from Problem Gravity (bombs), Utility Mapping (bottlenecks) and Operational
 * Gravity (fragile pillars), enriched by an optional NOAM synthesis.
 * Each card links directly to its parent module.
 */
export default function SystemicAnalysisTab() {
  const { t, i18n } = useTranslation();
  const [snap, setSnap] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/workspace/executive/systemic");
      setSnap(r.data);
    } catch (e) { setError(fmtError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const lang = (i18n.language || "fr").slice(0, 2);
      const r = await api.post("/workspace/executive/systemic/synthesis", { language: lang });
      setSnap(r.data.snapshot);
      setSynthesis(r.data.synthesis);
      notify.success(t("saas.systemic.synthesis_done", { defaultValue: "Synthèse NOAM générée." }));
    } catch (e) { notify.error(fmtError(e)); }
    finally { setGenerating(false); }
  };

  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const lang = (i18n.language || "fr").slice(0, 2);
      const r = await api.post("/workspace/executive/systemic/pdf", {
        language: lang, synthesis, ...snap,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `noam-systemic-${lang}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setPdfLoading(false); }
  };

  if (loading) {
    return <div className="text-zinc-400 text-sm">{t("common.loading", { defaultValue: "Chargement…" })}</div>;
  }
  if (error) {
    return <div className="text-[#E24B4A] text-sm" data-testid="systemic-error">{error}</div>;
  }

  const hasAny = (snap?.top_bombes?.length || 0)
    + (snap?.top_bottlenecks?.length || 0)
    + (snap?.top_piliers_fragiles?.length || 0) > 0;

  return (
    <div className="space-y-5 fade-up" data-testid="systemic-root">
      {/* Toolbar */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#7F77DD] noam-overline">
            <Sparkles className="w-3 h-3" /> {t("saas.systemic.overline", { defaultValue: "Lecture transversale" })}
          </div>
          <h2 className="noam-display text-xl font-bold tracking-tight mt-1">
            {t("saas.systemic.title", { defaultValue: "Analyse systémique" })}
          </h2>
          <p className="text-zinc-400 text-xs mt-1 max-w-3xl">
            {t("saas.systemic.subtitle", { defaultValue: "Croisement des trois lectures stratégiques : problèmes les plus lourds, acteurs centraux et piliers opérationnels fragiles." })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={generate} disabled={generating || !hasAny}
            className="btn-primary text-xs disabled:opacity-50" data-testid="systemic-generate-btn">
            {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("saas.systemic.generating", { defaultValue: "Génération…" })}</> :
              synthesis ? <><RefreshCw className="w-3.5 h-3.5" /> {t("saas.systemic.regenerate", { defaultValue: "Régénérer la synthèse" })}</> :
                <><Sparkles className="w-3.5 h-3.5" /> {t("saas.systemic.generate", { defaultValue: "Générer une synthèse IA" })}</>}
          </button>
          <button onClick={exportPdf} disabled={pdfLoading || !hasAny}
            className="btn-ghost text-xs disabled:opacity-50" data-testid="systemic-pdf-btn">
            {pdfLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> PDF…</> :
              <><Download className="w-3.5 h-3.5" /> {t("saas.systemic.export_pdf", { defaultValue: "Exporter PDF" })}</>}
          </button>
        </div>
      </div>

      {!hasAny ? (
        <EmptyState />
      ) : (
        <>
          {/* Synthesis card */}
          {synthesis && <SynthesisCard synthesis={synthesis} />}

          {/* Three columns */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ColumnCard
              icon={Bomb} color="#E24B4A"
              overline={t("saas.systemic.col_bombs_overline", { defaultValue: "Problem Gravity" })}
              title={t("saas.systemic.col_bombs_title", { defaultValue: "Problèmes les plus lourds" })}
              moduleLink="/app/problem-gravity"
              moduleLabel={t("saas.systemic.see_all", { defaultValue: "Voir tous" })}
              empty={t("saas.systemic.empty_problems", { defaultValue: "Aucun problème enregistré. Démarrez Problem Gravity." })}
              items={snap.top_bombes}
              renderItem={(b) => (
                <Link key={b.id} to={`/app/problem-gravity`} data-testid={`systemic-bomb-${b.id}`}
                  className="block p-2.5 rounded-md border border-white/5 hover:border-[#E24B4A]/40 hover:bg-[#E24B4A]/[0.04] transition group">
                  <div className="flex items-start gap-2">
                    <div className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                      style={{ background: "#E24B4A1A", color: "#E24B4A", border: "1px solid #E24B4A40" }}>
                      {b.gravity_score || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white truncate">{b.title}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span className="capitalize">{b.classification || "—"}</span>
                        {b.category && <><span className="opacity-30">·</span><span className="capitalize">{b.category}</span></>}
                      </div>
                    </div>
                    <ArrowUpRight className="w-3 h-3 text-zinc-600 group-hover:text-[#E24B4A] shrink-0 mt-0.5 transition" />
                  </div>
                </Link>
              )}
            />
            <ColumnCard
              icon={Network} color="#EF9F27"
              overline={t("saas.systemic.col_bottlenecks_overline", { defaultValue: "Utility Mapping" })}
              title={t("saas.systemic.col_bottlenecks_title", { defaultValue: "Acteurs les plus centraux" })}
              moduleLink="/app/utility-mapping"
              moduleLabel={t("saas.systemic.see_map", { defaultValue: "Voir la cartographie" })}
              empty={t("saas.systemic.empty_actors", { defaultValue: "Aucun acteur cartographié. Démarrez Utility Mapping." })}
              items={snap.top_bottlenecks}
              renderItem={(a) => (
                <Link key={a.id} to={`/app/utility-mapping`} data-testid={`systemic-bottleneck-${a.id}`}
                  className="block p-2.5 rounded-md border border-white/5 hover:border-[#EF9F27]/40 hover:bg-[#EF9F27]/[0.04] transition group">
                  <div className="flex items-start gap-2">
                    <div className="text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                      style={{ background: "#EF9F271A", color: "#EF9F27", border: "1px solid #EF9F2740" }}>
                      {a.centrality}%
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white truncate flex items-center gap-1.5">
                        {a.name}
                        {a.is_bottleneck && <Zap className="w-2.5 h-2.5 text-[#EF9F27] shrink-0" />}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span>↓{a.in_degree}</span><span>↑{a.out_degree}</span>
                        <span className="opacity-30">·</span><span className="capitalize">{a.criticite}</span>
                      </div>
                    </div>
                    <ArrowUpRight className="w-3 h-3 text-zinc-600 group-hover:text-[#EF9F27] shrink-0 mt-0.5 transition" />
                  </div>
                </Link>
              )}
            />
            <ColumnCard
              icon={Anchor} color="#7F77DD"
              overline={t("saas.systemic.col_fragiles_overline", { defaultValue: "Operational Gravity" })}
              title={t("saas.systemic.col_fragiles_title", { defaultValue: "Piliers les plus fragiles" })}
              moduleLink="/app/operational-gravity"
              moduleLabel={t("saas.systemic.see_pillars", { defaultValue: "Voir les centres" })}
              empty={t("saas.systemic.empty_pillars", { defaultValue: "Aucun centre de gravité défini. Démarrez Operational Gravity." })}
              items={snap.top_piliers_fragiles}
              renderItem={(c) => (
                <Link key={c.id} to={`/app/operational-gravity`} data-testid={`systemic-pillar-${c.id}`}
                  className="block p-2.5 rounded-md border border-white/5 hover:border-[#7F77DD]/40 hover:bg-[#7F77DD]/[0.04] transition group">
                  <div className="flex items-start gap-2">
                    <div className="shrink-0 mt-0.5 w-9">
                      <div className="text-[10px] font-mono text-center px-1 py-0.5 rounded"
                        style={{
                          background: c.robustesse < 40 ? "#E24B4A1A" : c.robustesse < 60 ? "#EF9F271A" : "#1D9E751A",
                          color: c.robustesse < 40 ? "#E24B4A" : c.robustesse < 60 ? "#EF9F27" : "#1D9E75",
                          border: `1px solid ${c.robustesse < 40 ? "#E24B4A40" : c.robustesse < 60 ? "#EF9F2740" : "#1D9E7540"}`,
                        }}>
                        {c.robustesse}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white truncate flex items-center gap-1.5">
                        {c.name}
                        {c.is_fragile && <ShieldOff className="w-2.5 h-2.5 text-[#E24B4A] shrink-0" />}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span className="capitalize">{c.criticite}</span>
                        {c.position_strategique && <><span className="opacity-30">·</span><span className="capitalize">{c.position_strategique}</span></>}
                      </div>
                      {c.vulnerabilites?.length > 0 && (
                        <div className="text-[10px] text-zinc-500 mt-1 italic line-clamp-1">{c.vulnerabilites[0]}</div>
                      )}
                    </div>
                    <ArrowUpRight className="w-3 h-3 text-zinc-600 group-hover:text-[#7F77DD] shrink-0 mt-0.5 transition" />
                  </div>
                </Link>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
}

function ColumnCard({ icon: Icon, color, overline, title, items, renderItem, moduleLink, moduleLabel, empty }) {
  return (
    <div className="noam-card p-4" style={{ borderColor: `${color}25` }}>
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 noam-overline" style={{ color }}>
            <Icon className="w-3 h-3" /> {overline}
          </div>
          <div className="noam-display text-sm font-semibold mt-1 truncate">{title}</div>
        </div>
      </div>
      {items?.length > 0 ? (
        <div className="space-y-1.5">
          {items.map(renderItem)}
        </div>
      ) : (
        <div className="text-xs text-zinc-500 italic py-4 text-center">{empty}</div>
      )}
      <div className="mt-3 pt-3 border-t border-white/5">
        <Link to={moduleLink} className="text-[11px] inline-flex items-center gap-1 hover:underline" style={{ color }}>
          {moduleLabel} <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function SynthesisCard({ synthesis }) {
  const { t } = useTranslation();
  return (
    <div className="noam-card p-5" style={{ borderColor: "#7F77DD40" }} data-testid="systemic-synthesis">
      <div className="noam-overline text-[#7F77DD] mb-2 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> {t("saas.systemic.synthesis", { defaultValue: "Synthèse NOAM" })}
      </div>
      {synthesis.diagnostic && (
        <p className="text-sm text-zinc-100 leading-relaxed">{synthesis.diagnostic}</p>
      )}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {synthesis.convergences?.length > 0 && (
          <Bullets title={t("saas.systemic.convergences", { defaultValue: "Convergences" })} items={synthesis.convergences} color="#1D9E75" />
        )}
        {synthesis.angles_morts?.length > 0 && (
          <Bullets title={t("saas.systemic.blind_spots", { defaultValue: "Angles morts" })} items={synthesis.angles_morts} color="#EF9F27" />
        )}
      </div>
      {synthesis.actions_prioritaires?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
            {t("saas.systemic.priority_actions", { defaultValue: "Actions prioritaires" })}
          </div>
          <ul className="space-y-1.5">
            {synthesis.actions_prioritaires.map((a, i) => {
              const p = (a.priorite || "").toLowerCase();
              const col = ["haute", "high", "hoch"].includes(p) ? "#E24B4A" :
                ["moyenne", "medium", "mittel"].includes(p) ? "#EF9F27" : "#71717a";
              return (
                <li key={i} className="text-xs flex items-start gap-2">
                  <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 mt-0.5 font-medium"
                    style={{ background: `${col}1A`, color: col, border: `1px solid ${col}40` }}>
                    {a.priorite}
                  </span>
                  <span className="text-zinc-200 flex-1">{a.action}</span>
                  {a.horizon && <span className="text-[10px] text-zinc-500 shrink-0">· {a.horizon}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {synthesis.lecture_dirigeant && (
        <div className="mt-4 pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {t("saas.systemic.boardroom", { defaultValue: "Lecture dirigeant" })}
          </div>
          <p className="text-sm italic text-[#B5B1F4] leading-relaxed">« {synthesis.lecture_dirigeant} »</p>
        </div>
      )}
    </div>
  );
}

function Bullets({ title, items, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color }}>{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-zinc-300 flex items-start gap-2">
            <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: color }} /> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="noam-card p-10 text-center" data-testid="systemic-empty">
      <div className="w-14 h-14 mx-auto rounded-xl bg-[#7F77DD]/10 border border-[#7F77DD]/30 flex items-center justify-center">
        <Target className="w-6 h-6 text-[#7F77DD]" />
      </div>
      <h3 className="noam-display text-xl font-semibold mt-4">
        {t("saas.systemic.empty_title", { defaultValue: "Trinité d'analyse non encore alimentée" })}
      </h3>
      <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
        {t("saas.systemic.empty_body", { defaultValue: "La lecture systémique croise vos problèmes lourds, vos goulots d'écosystème et vos piliers opérationnels. Démarrez par l'un des trois modules." })}
      </p>
      <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
        <Link to="/app/problem-gravity" className="btn-ghost text-sm">
          <Bomb className="w-4 h-4 text-[#E24B4A]" /> Problem Gravity
        </Link>
        <Link to="/app/utility-mapping" className="btn-ghost text-sm">
          <Network className="w-4 h-4 text-[#EF9F27]" /> Utility Mapping
        </Link>
        <Link to="/app/operational-gravity" className="btn-ghost text-sm">
          <Anchor className="w-4 h-4 text-[#7F77DD]" /> Operational Gravity
        </Link>
      </div>
    </div>
  );
}
